import { useState } from 'react';
import { useCreateReview } from '@/hooks/useReviews';
import { Button } from '@/components/ui/Button';
import { haptic } from '@/lib/telegram';
import type { AuctionDetail } from '@/types/domain';
import styles from './ReviewPanel.module.css';

interface Props {
  auction: AuctionDetail;
  myUserId: string;
}

export const ReviewPanel = ({ auction, myUserId }: Props) => {
  const create = useCreateReview(auction.id);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');

  // Только для проданных лотов и участников сделки
  if (auction.status !== 'sold') return null;
  const isSeller = auction.seller.id === myUserId;
  const isWinner = auction.current_leader_id === myUserId;
  if (!isSeller && !isWinner) return null;

  const otherPartyLabel = isSeller ? 'покупателе' : 'продавце';

  // Уже оставил отзыв — показываем его
  if (auction.my_review) {
    const r = auction.my_review;
    return (
      <div className={styles.panel}>
        <p className={styles.heading}>Твой отзыв</p>
        <div className={styles.stars}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} filled={s <= r.rating} />
          ))}
        </div>
        {r.comment && <p className={styles.myComment}>{r.comment}</p>}
      </div>
    );
  }

  const handleSubmit = () => {
    if (rating === 0) return;
    haptic.medium();
    create.mutate({ rating, comment: comment.trim() || null });
  };

  return (
    <div className={styles.panel}>
      <p className={styles.heading}>Оставь отзыв о {otherPartyLabel}</p>
      <p className={styles.hint}>Сделка завершена. Оцени, как всё прошло.</p>

      {/* Звёзды */}
      <div
        className={styles.starsInput}
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            type="button"
            className={styles.starBtn}
            onMouseEnter={() => setHover(s)}
            onClick={() => { haptic.light(); setRating(s); }}
            aria-label={`${s} звёзд`}
          >
            <Star filled={s <= (hover || rating)} />
          </button>
        ))}
      </div>

      <textarea
        className={styles.comment}
        placeholder="Комментарий (необязательно)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={2000}
      />

      <Button
        variant="primary"
        fullWidth
        disabled={rating === 0}
        loading={create.isPending}
        onClick={handleSubmit}
      >
        Отправить отзыв
      </Button>

      {create.isError && (
        <p className={styles.error}>
          {(create.error as Error)?.message ?? 'Не удалось отправить отзыв'}
        </p>
      )}
    </div>
  );
};

const Star = ({ filled }: { filled: boolean }) => (
  <svg
    width="28" height="28" viewBox="0 0 24 24"
    fill={filled ? '#d4a52a' : 'none'}
    stroke={filled ? '#d4a52a' : 'currentColor'}
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);